import React, { useRef, useState } from 'react';
import { IoInformationCircleOutline } from "react-icons/io5";
import EmployeeForm from './EmployeeForm';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../components/Config/api.service';
import EmployeeSelect from '../../components/Formulaire/EmployeeSelect';
const EmployeeNew = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [showSingleForm, setShowSingleForm] = useState(true);
    const [showMultipleForm, setShowMultipleForm] = useState(true);
    const [employees, setEmployees] = useState([
        { firstName: '', lastName: '', email: '' },
        { firstName: '', lastName: '', email: '' },
        { firstName: '', lastName: '', email: '' },
    ]);

    // Création par duplication (YRH-SHARED-010-T2, Yros/Kavero#195). Le serveur dit
    // ce qui se copie de l'employé choisi (`modele`) ; le formulaire s'en
    // pré-remplit. Changer de source garde le formulaire ouvert : ce que la RH a
    // déjà saisi ne disparaît pas. Seul « Partir de zéro » le vide.
    const [mode, setMode] = useState('zero');
    const [source, setSource] = useState(null);
    const [modele, setModele] = useState(null);
    const [choixOuvert, setChoixOuvert] = useState(false);
    const [chargementModele, setChargementModele] = useState(false);
    const [erreurModele, setErreurModele] = useState('');
    const [cleFormulaire, setCleFormulaire] = useState(0);
    // Seule la dernière lecture compte : la réponse d'un choix supplanté est jetée.
    const derniereLecture = useRef(0);

    const choisirSource = async (option) => {
        setSource(option);
        setErreurModele('');
        if (!option) return;
        const lecture = ++derniereLecture.current;
        setChargementModele(true);
        try {
            const reponse = await apiService.lireModeleDuplication(option.value);
            if (lecture !== derniereLecture.current) return;
            if (!reponse?.modele) throw new Error('modèle absent');
            setModele(reponse.modele);
            setChoixOuvert(false);
        } catch (erreur) {
            if (lecture !== derniereLecture.current) return;
            // Le message du serveur est écrit pour l'utilisateur ; sans lui, le texte
            // générique. Un modèle déjà chargé reste en place.
            const message = erreur?.response?.data?.message;
            setErreurModele(typeof message === 'string' && message
                ? message : t('employeeNew.duplication.erreur'));
        } finally {
            if (lecture === derniereLecture.current) setChargementModele(false);
        }
    };

    const choisirMode = (suivant) => {
        setMode(suivant);
        setErreurModele('');
    };

    const partirDeZero = () => {
        derniereLecture.current += 1;
        setMode('zero');
        setSource(null);
        setModele(null);
        setChoixOuvert(false);
        setChargementModele(false);
        setErreurModele('');
        setCleFormulaire(cle => cle + 1);
    };

    const afficherChoix = !modele || choixOuvert;
    const afficherFormulaire = mode === 'zero' || Boolean(modele);

    const handleEmployeeChange = (index, field, value) => {
        const updatedEmployees = [...employees];
        updatedEmployees[index][field] = value;
        setEmployees(updatedEmployees);
    };

    const addEmployee = () => {
        setEmployees([...employees, { firstName: '', lastName: '', email: '' }]);
    };

    const onSuccess = (employeeId) => {
        console.log('onSuccess employeeId ', employeeId)
        employeeId && navigate('/employees/'+employeeId);
    }

    return (
        <div>
            <div className="flex items-center gap-4 justify-start mb-4">
                <FaArrowLeft className="text-[--color-primary]" />
                <a href="/employees" className="text-[--color-primary]">
                    {t('employeeNew.backToList')}
                </a>
            </div>
            <div className="bg-white shadow-md rounded-lg p-6 mb-6">
                <h1 className='text-3xl font-[700] mb-4'>
                    {t('employeeNew.title')}
                </h1>
                <p className='text-lg text-gray-500 mb-4'>
                    {t('employeeNew.subtitle')}
                </p>

                {afficherChoix && (
                    <fieldset
                        data-testid="duplication-choix"
                        className="mb-6 rounded-lg border-2 border-[--color-primary] bg-[--color-primary]/5 p-5"
                    >
                        <legend className="px-2 text-sm font-semibold text-[--color-primary]">
                            {t('employeeNew.duplication.depart')}
                        </legend>
                        <div className={`grid grid-cols-1 gap-4 md:grid-cols-2${modele ? ' hidden' : ''}`}>
                            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-300 bg-white p-4">
                                <input
                                    type="radio"
                                    name="depart"
                                    data-testid="duplication-mode-zero"
                                    checked={mode === 'zero'}
                                    onChange={() => choisirMode('zero')}
                                    className="mt-1"
                                />
                                <span>
                                    <span className="block font-semibold">{t('employeeNew.duplication.deZero')}</span>
                                    <span className="block text-sm text-gray-500">{t('employeeNew.duplication.deZeroAide')}</span>
                                </span>
                            </label>
                            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-300 bg-white p-4">
                                <input
                                    type="radio"
                                    name="depart"
                                    data-testid="duplication-mode-existant"
                                    checked={mode === 'existant'}
                                    onChange={() => choisirMode('existant')}
                                    className="mt-1"
                                />
                                <span>
                                    <span className="block font-semibold">{t('employeeNew.duplication.depuisExistant')}</span>
                                    <span className="block text-sm text-gray-500">{t('employeeNew.duplication.depuisExistantAide')}</span>
                                </span>
                            </label>
                        </div>
                        {mode === 'existant' && (
                            <div className="mt-4 max-w-xl">
                                <div className="mb-1 text-sm font-semibold">{t('employeeNew.duplication.source')}</div>
                                <EmployeeSelect
                                    value={source}
                                    onChange={choisirSource}
                                    placeholder={t('employeeNew.duplication.sourcePlaceholder')}
                                />
                                {chargementModele && (
                                    <p data-testid="duplication-chargement" className="mt-2 text-sm text-gray-500">
                                        {t('employeeNew.duplication.chargement')}
                                    </p>
                                )}
                            </div>
                        )}
                        {erreurModele && (
                            <p
                                role="alert"
                                data-testid="duplication-erreur"
                                className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                            >
                                {erreurModele}
                            </p>
                        )}
                    </fieldset>
                )}

                {/* Bouton pour afficher le formulaire d'un seul employé */}
                <div className='border border-gray-300 rounded-lg p-6 mb-6 flex items-center'>
                    <button
                        onClick={() => setShowSingleForm(!showSingleForm)}
                        className="flex items-center w-full"
                    >
                        <IoInformationCircleOutline className='text-xl' />
                        <div className='ml-2 text-lg font-[500]'>
                            {t('employeeNew.toggleFormLabel')}
                        </div>
                    </button>
                </div>
                {showSingleForm && afficherFormulaire && (
                    <div className="px-20 mb-4 rounded-lg">
                        <EmployeeForm
                            key={cleFormulaire}
                            onSuccess={onSuccess}
                            modele={modele}
                            onChangerSource={() => { setChoixOuvert(true); setErreurModele(''); }}
                            onPartirDeZero={partirDeZero}
                        />
                    </div>
                )}

                {/*
                <div className='border border-gray-300 rounded-lg p-6 mb-4 flex items-center'>
                    <button
                        onClick={() => setShowMultipleForm(!showMultipleForm)}
                        className="flex items-center w-full"
                    >
                        <IoInformationCircleOutline className='text-xl' />
                        <div className='ml-2 text-lg font-[500]'>
                            Gagner du temps, vos collaborateurs recevront un mail pour remplir leurs informations
                        </div>
                    </button>
                </div>
                {showMultipleForm && (
                    <div className="p-4">
                        <h2 className="text-xl font-bold mb-4">Formulaire pour plusieurs employés</h2>
                        <form>
                            {employees.map((employee, index) => (
                                <div key={index} className="flex items-center space-x-4 mb-4">
                                    <input
                                        type="text"
                                        placeholder="Prénom"
                                        value={employee.firstName}
                                        onChange={(e) => handleEmployeeChange(index, 'firstName', e.target.value)}
                                        className="border rounded-lg p-2 w-1/3"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Nom"
                                        value={employee.lastName}
                                        onChange={(e) => handleEmployeeChange(index, 'lastName', e.target.value)}
                                        className="border rounded-lg p-2 w-1/3"
                                    />
                                    <input
                                        type="email"
                                        placeholder="Email"
                                        value={employee.email}
                                        onChange={(e) => handleEmployeeChange(index, 'email', e.target.value)}
                                        className="border rounded-lg p-2 w-1/3"
                                    />
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={addEmployee}
                                className=" text-[--color-primary] font-semibold mb-4"
                            >
                                + Ajouter un collaborateur
                            </button>
                            <div className="text-center">
                                <button
                                    type="submit"
                                    className="bg-[--color-primary] rounded-full text-white px-12 py-2"
                                >
                                    Valider
                                </button>
                            </div>
                        </form>
                    </div>
                )}
                */}
            </div>
        </div>
    );
}

export default EmployeeNew;